import React, { useState, useCallback, useEffect } from 'react';
import { Modal, ModalContent } from '../shared/Modal';
import { Input } from '../shared/Input';
import { Button } from '../shared/Button';
import { useTheme } from '../../contexts/ThemeContext';
import { ADD_ENTITY_CONFIGS } from '../../config/constants';
import { EntityFieldConfig, EntityKey } from '../../types';

interface AddEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityKey;
  onSubmit: (data: Record<string, string>) => Promise<void>;
  error?: string | null;
}

export const AddEntityModal: React.FC<AddEntityModalProps> = ({
  isOpen,
  onClose,
  entityType,
  onSubmit,
  error,
}) => {
  const { darkMode } = useTheme();
  const config = ADD_ENTITY_CONFIGS[entityType];
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableCredit, setAvailableCredit] = useState('');
  const [otherFields, setOtherFields] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialData: Record<string, string> = {};
      config.fields.forEach((field: EntityFieldConfig) => {
        initialData[field.name] = field.defaultValue || '';
      });
      setFormData(initialData);
      setAvailableCredit('');
      setOtherFields({});
    }
  }, [isOpen, config.fields]);

  const handleFieldChange = useCallback(
    (fieldName: string, value: string, transform?: (v: string) => string) => {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: transform ? transform(value) : value,
      }));
    },
    []
  );

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent double submission

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      // Don't call onClose() here - parent handles closing on success
    } catch (err) {
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalContent>
        <h3 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          {config.title}
        </h3>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        <div className="space-y-4 mb-6">
          {config.fields.map((field: EntityFieldConfig, index: number) =>
            field.type === 'select' && field.options ? (
              <div key={field.name}>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {field.label}
                </label>
                <select
                  value={
                    field.allowOther && otherFields[field.name] !== undefined
                      ? '__other__'
                      : formData[field.name] || field.defaultValue || ''
                  }
                  onChange={(e) => {
                    if (field.allowOther && e.target.value === '__other__') {
                      setOtherFields(prev => ({ ...prev, [field.name]: '' }));
                      handleFieldChange(field.name, '');
                    } else {
                      setOtherFields(prev => {
                        const next = { ...prev };
                        delete next[field.name];
                        return next;
                      });
                      handleFieldChange(field.name, e.target.value);
                    }
                  }}
                  className={`w-full px-3 py-2 rounded-lg text-sm border ${
                    darkMode
                      ? 'bg-slate-800 border-slate-600 text-white'
                      : 'bg-white border-slate-200 text-slate-900'
                  }`}
                >
                  {field.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                  {field.allowOther && <option value="__other__">Custom…</option>}
                </select>
                {field.allowOther && otherFields[field.name] !== undefined && (
                  <input
                    type="text"
                    value={otherFields[field.name]}
                    onChange={(e) => {
                      const val = e.target.value;
                      setOtherFields(prev => ({ ...prev, [field.name]: val }));
                      handleFieldChange(field.name, val);
                    }}
                    placeholder={field.placeholder}
                    autoFocus
                    className={`w-full mt-2 px-3 py-2 rounded-lg text-sm border ${
                      darkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                )}
              </div>
            ) : (
              <Input
                key={field.name}
                type={field.type}
                label={field.label}
                placeholder={field.placeholder}
                value={formData[field.name] || ''}
                onChange={(e) =>
                  handleFieldChange(field.name, e.target.value, field.transform)
                }
                step={field.step}
                helpText={field.helpText}
                autoFocus={index === 0}
              />
            )
          )}

          {entityType === 'creditCard' && (
            <>
              <div className={`relative flex items-center gap-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <div className="flex-1 border-t border-current opacity-30" />
                <span className="text-xs uppercase tracking-wider">or</span>
                <div className="flex-1 border-t border-current opacity-30" />
              </div>
              <Input
                type="number"
                label="Available Credit"
                placeholder="e.g., 4980.30"
                value={availableCredit}
                onChange={(e) => {
                  const val = e.target.value;
                  setAvailableCredit(val);
                  const limit = parseFloat(formData['limit'] || '0');
                  const available = parseFloat(val || '0');
                  if (limit > 0 && available >= 0) {
                    setFormData((prev) => ({
                      ...prev,
                      balance: Math.max(0, limit - available).toFixed(2),
                    }));
                  }
                }}
                step="0.01"
                helpText="Enter what your card says you have left to spend — we'll calculate the balance"
              />
            </>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleCancel}
            className="flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : config.submitLabel}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};
